import { useState, useMemo, useEffect, useRef } from 'react'
import { Calculator, TrendingUp, DollarSign, Info } from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'N/A'
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (absValue >= 1000) return `${sign}$${(absValue / 1000).toFixed(1)}B`
  if (absValue < 1) return `${sign}$${(absValue * 1000).toFixed(0)}k`
  return `${sign}$${absValue.toFixed(1)}M`
}

const formatPercent = (value) => {
  if (!value && value !== 0) return 'N/A'
  return `${value.toFixed(1)}%`
}

export default function ROIAnalysis({ target, valuationData }) {
  // Get base revenue from valuation data
  const baseRevenue = valuationData?.revenue || target.revenue || 10

  // Calculate initial acquisition price from valuation data
  const getInitialAcquisitionPrice = () => {
    if (valuationData?.predicted) return valuationData.predicted
    if (target.estimatedValue) return target.estimatedValue
    return 25
  }

  // Input states
  const [acquisitionPrice, setAcquisitionPrice] = useState(getInitialAcquisitionPrice)
  const [revenueGrowthPct, setRevenueGrowthPct] = useState(15)
  const [ebitdaPct, setEbitdaPct] = useState(valuationData?.ebitdaPct || 15)
  const [upsellPct, setUpsellPct] = useState(5)
  const [crossSellPct, setCrossSellPct] = useState(3)
  const [costSynergyPct, setCostSynergyPct] = useState(5)

  // Acquisition outflow timing (percentage paid in each year)
  const [outflowYear1, setOutflowYear1] = useState(100)
  const [outflowYear2, setOutflowYear2] = useState(0)
  const [outflowYear3, setOutflowYear3] = useState(0)
  const [outflowLater, setOutflowLater] = useState(0)

  // Track if user has manually modified acquisition price
  const userModifiedRef = useRef(false)

  // Update acquisition price when valuationData changes
  useEffect(() => {
    if (valuationData?.predicted && !userModifiedRef.current) {
      setAcquisitionPrice(valuationData.predicted)
    }
    if (valuationData?.ebitdaPct) {
      setEbitdaPct(valuationData.ebitdaPct)
    }
  }, [valuationData])

  // Calculate ROI with acquisition outflow timing
  const analysis = useMemo(() => {
    const years = []
    let cumulativeCashFlow = 0
    let paybackYear = null

    // Base EBITDA for synergy calculation
    const baseEbitda = baseRevenue * (ebitdaPct / 100)

    // Calculate outflow for each year based on timing percentages
    const getOutflowForYear = (year) => {
      if (year === 1) return acquisitionPrice * (outflowYear1 / 100)
      if (year === 2) return acquisitionPrice * (outflowYear2 / 100)
      if (year === 3) return acquisitionPrice * (outflowYear3 / 100)
      if (year === 4) return acquisitionPrice * (outflowLater / 100) // "Later" treated as Year 4
      return 0
    }

    for (let year = 1; year <= 7; year++) {
      // Revenue grows annually
      const revenueGrowth = Math.pow(1 + revenueGrowthPct / 100, year)
      // Additional revenue from upsell and cross-sell (cumulative each year)
      const upsellCrossSell = 1 + ((upsellPct + crossSellPct) / 100) * year

      const yearRevenue = baseRevenue * revenueGrowth * upsellCrossSell

      // EBITDA = Revenue * EBITDA %
      const yearEbitda = yearRevenue * (ebitdaPct / 100)

      // Cost synergies add to EBITDA (as % of base EBITDA)
      const synergies = baseEbitda * (costSynergyPct / 100)

      const totalYearEbitda = yearEbitda + synergies

      // Subtract acquisition outflow for this year
      const yearOutflow = getOutflowForYear(year)
      const netCashFlow = totalYearEbitda - yearOutflow

      cumulativeCashFlow += netCashFlow

      years.push({
        year: `Year ${year}`,
        ebitda: totalYearEbitda,
        outflow: yearOutflow,
        netCashFlow,
        cumulative: cumulativeCashFlow,
        isPayback: cumulativeCashFlow >= 0 && paybackYear === null
      })

      if (cumulativeCashFlow >= 0 && paybackYear === null) {
        paybackYear = year
      }
    }

    // Calculate IRR (simplified Newton-Raphson) with timing-adjusted cash flows
    const cashFlows = years.map(y => y.netCashFlow)
    let irr = 0.1
    for (let i = 0; i < 100; i++) {
      let npv = 0
      let dnpv = 0
      for (let t = 0; t < cashFlows.length; t++) {
        npv += cashFlows[t] / Math.pow(1 + irr, t + 1)
        dnpv -= (t + 1) * cashFlows[t] / Math.pow(1 + irr, t + 2)
      }
      if (Math.abs(npv) < 0.0001) break
      irr = irr - npv / dnpv
    }

    const totalCashFlow = years.reduce((sum, y) => sum + y.ebitda, 0)
    const totalROI = ((totalCashFlow - acquisitionPrice) / acquisitionPrice) * 100

    return {
      years,
      paybackYear: paybackYear || '>7',
      totalROI,
      irr: isNaN(irr) ? 0 : irr * 100,
      totalCashFlow
    }
  }, [acquisitionPrice, baseRevenue, revenueGrowthPct, ebitdaPct, upsellPct, crossSellPct, costSynergyPct, outflowYear1, outflowYear2, outflowYear3, outflowLater])

  return (
    <div className="space-y-6">
      {/* EBITDA-based Methodology Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">EBITDA-Based Cash Flow Analysis</p>
          <p className="text-xs text-blue-700 mt-1">
            This ROI model uses EBITDA (Earnings Before Interest, Taxes, Depreciation & Amortization) as the proxy for operating cash flow.
            Unlike PAT (Profit After Tax), EBITDA provides a cleaner view of operational performance and is the standard metric for M&A valuation.
          </p>
        </div>
      </div>

      {/* Main content - side by side */}
      <div className="grid grid-cols-5 gap-6">
        {/* Left: Input controls */}
        <div className="col-span-2 bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-sm font-semibold text-neutral-900 mb-4">Scenario Inputs</h3>

          <div className="space-y-5">
            <div>
              <label className="block text-sm text-neutral-600 mb-2">Acquisition Price ($ Million)</label>
              <input
                type="number"
                step="0.1"
                value={Math.round(acquisitionPrice * 10) / 10}
                onChange={(e) => {
                  userModifiedRef.current.acquisition = true
                  setAcquisitionPrice(parseFloat(e.target.value) || 0)
                }}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>

            {/* Acquisition Outflow Timing */}
            <div className="pt-4 border-t border-neutral-100">
              <label className="block text-sm font-medium text-neutral-700 mb-3">Acquisition Outflow Timing</label>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Year 1</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={outflowYear1}
                      onChange={(e) => setOutflowYear1(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="w-full h-9 px-2 pr-6 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Year 2</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={outflowYear2}
                      onChange={(e) => setOutflowYear2(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="w-full h-9 px-2 pr-6 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Year 3</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={outflowYear3}
                      onChange={(e) => setOutflowYear3(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="w-full h-9 px-2 pr-6 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Later</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={outflowLater}
                      onChange={(e) => setOutflowLater(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="w-full h-9 px-2 pr-6 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
                  </div>
                </div>
              </div>
              {(outflowYear1 + outflowYear2 + outflowYear3 + outflowLater) !== 100 && (
                <p className="text-xs text-amber-600 mt-2">
                  Total must equal 100% (currently {outflowYear1 + outflowYear2 + outflowYear3 + outflowLater}%)
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-600 mb-2">
                Revenue Growth: {revenueGrowthPct}%
              </label>
              <input
                type="range"
                min="0"
                max="40"
                value={revenueGrowthPct}
                onChange={(e) => setRevenueGrowthPct(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-neutral-400 mt-1">
                <span>0%</span>
                <span>40%</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-neutral-600 mb-2">
                EBITDA Margin: {ebitdaPct}%
              </label>
              <input
                type="range"
                min="5"
                max="50"
                value={ebitdaPct}
                onChange={(e) => setEbitdaPct(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-neutral-400 mt-1">
                <span>5% (Low)</span>
                <span>50% (High)</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-neutral-600 mb-2">
                Upsell Revenue: {upsellPct}%
              </label>
              <input
                type="range"
                min="0"
                max="20"
                value={upsellPct}
                onChange={(e) => setUpsellPct(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-neutral-400 mt-1">
                <span>0%</span>
                <span>20%</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-neutral-600 mb-2">
                Cross-sell Revenue: {crossSellPct}%
              </label>
              <input
                type="range"
                min="0"
                max="20"
                value={crossSellPct}
                onChange={(e) => setCrossSellPct(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-neutral-400 mt-1">
                <span>0%</span>
                <span>20%</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-neutral-600 mb-2">
                Cost Synergies: {costSynergyPct}%
              </label>
              <input
                type="range"
                min="0"
                max="20"
                value={costSynergyPct}
                onChange={(e) => setCostSynergyPct(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-neutral-400 mt-1">
                <span>0%</span>
                <span>20%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Summary + Chart */}
        <div className="col-span-3 bg-white rounded-lg border border-neutral-200 p-6">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
              <p className="text-xs text-primary-600">Payback Period</p>
              <p className="text-2xl font-semibold text-primary-700 mt-1">
                {typeof analysis.paybackYear === 'number' ? `${analysis.paybackYear}yr` : analysis.paybackYear}
              </p>
              <p className="text-xs text-primary-500 mt-1">EBITDA-based</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <p className="text-xs text-neutral-500">7-Year ROI</p>
              <p className="text-2xl font-semibold text-neutral-900 mt-1">{analysis.totalROI.toFixed(0)}%</p>
              <p className="text-xs text-neutral-400 mt-1">Total return</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
              <p className="text-xs text-emerald-600">IRR</p>
              <p className="text-2xl font-semibold text-emerald-700 mt-1">{analysis.irr.toFixed(1)}%</p>
              <p className="text-xs text-emerald-500 mt-1">Internal rate of return</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <p className="text-xs text-neutral-500">Total EBITDA</p>
              <p className="text-2xl font-semibold text-neutral-900 mt-1">{formatCurrency(analysis.totalCashFlow)}</p>
              <p className="text-xs text-neutral-400 mt-1">7-year cumulative</p>
            </div>
          </div>

          {/* Chart */}
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analysis.years}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="year" tick={{ fill: '#71717A', fontSize: 12 }} />
                <YAxis tick={{ fill: '#71717A', fontSize: 12 }} tickFormatter={(v) => `$${v.toFixed(0)}M`} />
                <Tooltip
                  formatter={(value) => [`$${value.toFixed(1)}M`, '']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E4E4E7' }}
                />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="#3B82F6"
                  fill="#DBEAFE"
                  strokeWidth={2}
                  name="Cumulative Cash Flow"
                />
                <Area
                  type="monotone"
                  dataKey="ebitda"
                  stroke="#10B981"
                  fill="#D1FAE5"
                  strokeWidth={2}
                  name="Annual EBITDA"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-primary-400"></span>
              Cumulative EBITDA (Net of Acquisition)
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-emerald-400"></span>
              Annual EBITDA
            </span>
          </div>
        </div>
      </div>

      {/* Year by year breakdown */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-neutral-900">EBITDA Cash Flow Projection</h3>
          <span className="text-xs text-neutral-400">Annual EBITDA as operating cash proxy</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200">
              <th className="text-left py-3 font-medium text-neutral-500">Year</th>
              <th className="text-right py-3 font-medium text-neutral-500">EBITDA</th>
              <th className="text-right py-3 font-medium text-neutral-500">Outflow</th>
              <th className="text-right py-3 font-medium text-neutral-500">Net Cash Flow</th>
              <th className="text-right py-3 font-medium text-neutral-500">Cumulative</th>
              <th className="text-right py-3 font-medium text-neutral-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {analysis.years.map((year, idx) => (
              <tr key={idx} className="border-b border-neutral-100">
                <td className="py-3 text-neutral-800">{year.year}</td>
                <td className="py-3 text-right text-emerald-600">{formatCurrency(year.ebitda)}</td>
                <td className="py-3 text-right text-red-600">
                  {year.outflow > 0 ? formatCurrency(-year.outflow) : '-'}
                </td>
                <td className={`py-3 text-right font-medium ${year.netCashFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(year.netCashFlow)}
                </td>
                <td className={`py-3 text-right ${year.cumulative >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(year.cumulative)}
                </td>
                <td className="py-3 text-right">
                  {year.isPayback ? (
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-md font-medium">
                      Payback
                    </span>
                  ) : year.cumulative >= 0 ? (
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-600 text-xs rounded-md">Profitable</span>
                  ) : (
                    <span className="px-2 py-1 bg-amber-50 text-amber-600 text-xs rounded-md">Recovering</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
